import Modal from '@/components/modals/Modal';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import YouTubeVideo from '@/components/YouTubeVideo';
import { COLORS } from '@/styles/color';
import { marginY, mediaBreakpointDown, mediaBreakpointOnly, mediaBreakpointUp, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import styled from 'styled-components';

const PAGE_GRADIENT_URL = '/assets/pages/resource-download/gradient-background.jpg';

const Eyebrow = styled.span`
  display: flex;
  column-gap: ${remCalc(10)};
  ${customTextSize(12, 15)}
  font-weight: ${FONT_WEIGHTS.NORMAL};
  letter-spacing: 0.08em;
  text-transform: uppercase;

  ${mediaBreakpointOnly('mobile')} {
    margin-bottom: ${remCalc(10)};
  }

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(14, 18)}
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(16, 20)}
  }
`;

const PageHeadline = styled.h1`
  ${customTextSize(24, 30)};
  letter-spacing: -0.01em;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};

  ${mediaBreakpointOnly('mobile')} {
    margin-bottom: ${remCalc(16)};
  }

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(32, 38)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(38, 46)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(50, 58)};
  }
`;

const CoverImageWrapper = styled.div`
  display: flex;
  justify-self: center;

  img {
    border-radius: ${remCalc(10)};
    width: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    grid-column: -1 / -7;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 1 / 10;
    justify-self: auto;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 2 / 9;
  }
`;

const HeaderTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  && > p {
    margin-bottom: 0;

    ${mediaBreakpointOnly('mobile')} {
      margin-bottom: ${remCalc(20)};
    }
  }

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / 7;
    row-gap: ${remCalc(20)};

    && > p {
      ${customTextSize(16, 24)};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(24)};
    margin-bottom: ${remCalc(50)};
    grid-column: 1 / 10;
    && > p {
      ${customTextSize(24, 34)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 2 / 10;
  }
`;

const FormStickyWrapper = styled.div`
  ${mediaBreakpointUp('desktop')} {
    position: sticky;
    top: ${remCalc(PRIMARY_NAV_HEIGHT)};
  }
`;

const MainContentWrapper = styled.div`
  padding: ${remCalc(30, 20)};

  ${mediaBreakpointUp('tablet')} {
    padding: ${remCalc(40, 44)};
  }
  ${mediaBreakpointUp('desktop')} {
    padding: 0;
  }
`;

const MainContentBackgroundContainer = styled.div`
  background: url(${PAGE_GRADIENT_URL}) no-repeat;
  background-size: cover;
  border-radius: ${remCalc(5)};

  ${mediaBreakpointUp('desktop')} {
    background: none;
  }
`;

const ResourceDownloadPageWrapper = styled.div`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: repeat(2, auto);
  column-gap: ${remCalc(20)};
  row-gap: ${remCalc(20)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: repeat(12, 1fr);
    row-gap: ${remCalc(30)};
  }

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(18, 1fr);
    position: relative;
    row-gap: ${remCalc(50)};

    &::before {
      content: '';
      position: absolute;
      background: url(${PAGE_GRADIENT_URL}) no-repeat;
      background-size: cover;
      height: 100%;
      left: calc((100vw - 100%) / -2);
      width: calc(54.5% + ((100vw - 100%) / 2));
      z-index: -1;
    }
  }
`;

const HeaderContainer = styled.div`
  grid-row: 1;
  grid-column: 1 / span 6;
  position: relative;

  ${mediaBreakpointDown('tablet')} {
    &::before {
      content: '';
      position: absolute;
      background: url(${PAGE_GRADIENT_URL}) no-repeat;
      background-size: cover;
      height: 100%;
      left: calc((100vw - 100%) / -2);
      width: 100vw;
      z-index: -1;
    }
  }

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / span 12;
    display: flex;
    column-gap: ${remCalc(20)};
    > div {
      flex: 1;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 1 / span 9;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 1 / span 10;
  }
`;

const HeaderContentWrapper = styled.div`
  ${paddingY(30)}
  display: flex;
  flex-direction: column;

  ${mediaBreakpointUp('tablet')} {
    display: flex;
    flex-direction: row;
    column-gap: ${remCalc(20)};
    ${paddingY(40)}

    > div {
      flex: 1;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    flex-direction: column;
    ${paddingY(60, 0)}
  }

  ${mediaBreakpointUp('desktopWide')} {
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    column-gap: ${remCalc(20)};
  }
`;

const FormContainer = styled.div`
  grid-column: 1 / span 6;
  grid-row: 2;

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / span 12;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 11 / 19;
    grid-row: 1 / -1;

    ${paddingY(60, 80)}
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column-start: 12;
  }
`;

const ContentContainer = styled.div`
  grid-column: 1 / span 6;

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / span 12;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 1 / span 9;
    padding-bottom: ${remCalc(60)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 2 / span 7;
    padding-bottom: ${remCalc(80)};
  }
`;

const LiveIndicator = styled.div`
  ${customTextSize(12, 18)}
  background-color: ${COLORS.RED.BASE};
  border-radius: ${remCalc(8, 8, 8, 2)};
  padding: ${remCalc(4, 8)};
  color: ${COLORS.WHITE};

  display: inline-flex;
  align-items: center;

  height: ${remCalc(24)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(16, 26)}
    height: ${remCalc(42)};
    border-radius: ${remCalc(10, 10, 10, 2)};
    padding: ${remCalc(8, 16)};
  }

  svg {
    width: ${remCalc(16)};
    height: ${remCalc(16)};
    margin-right: ${remCalc(8)};

    ${mediaBreakpointUp('desktop')} {
      width: ${remCalc(24)};
      height: ${remCalc(24)};
      margin-right: ${remCalc(12)};
    }
  }

  strong {
    font-weight: ${FONT_WEIGHTS.BOLD};
    margin-right: ${remCalc(8)};
  }
`;

const WebinarSpeakerHeadline = styled.h5`
  ${customTextSize(24, 30)}
  margin-top: ${remCalc(82)};
  margin-bottom: ${remCalc(28)};
`;

const WebinarSpeakerList = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(24)};
  ${mediaBreakpointOnly('mobile')} {
    padding-left: 0;
  }
`;

const WebinarSpeaker = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;

  h6 {
    span {
      display: block;

      /* Name */
      &:nth-child(1) {
        ${customTextSize(16, 26)}
        font-weight: ${FONT_WEIGHTS.BOLD};
      }

      /* Invisible comma */
      &:nth-child(2) {
        visibility: hidden;
        position: absolute;
        top: -9999px;
        left: -9999px;
      }

      /* Title */
      &:nth-child(3) {
        ${customTextSize(16, 26)}
        font-weight: ${FONT_WEIGHTS.NORMAL};
      }
    }
  }
`;

const WebinarSpeakerImage = styled.div<{ objectPosition: string }>`
  position: relative;
  width: ${remCalc(80)};
  height: ${remCalc(80)};
  border-radius: 100%;
  overflow: hidden;
  margin-right: ${remCalc(24)};
  flex-shrink: 0;

  img {
    object-fit: cover;
    object-position: ${(props) => props.objectPosition};
  }
`;

const VideoModalYouTubeVideo = styled(YouTubeVideo)`
  ${mediaBreakpointUp('desktopWide')} {
    height: 100%;
    padding-bottom: 0;
  }
`;

const VideoModal = styled(Modal)`
  ${mediaBreakpointUp('desktopWide')} {
    --modal-height: 100%;
  }
`;

const RelatedContainer = styled.div`
  ${marginY(32)};

  ${mediaBreakpointUp('desktop')} {
    position: relative;
    padding-top: 2rem;
    margin-top: 0;

    &::before {
      content: '';
      position: absolute;
      top: -5rem;
      left: 0;
      width: 100%;
      height: 5rem;
      background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
    }
  }
`;

const resourcePageComponents = {
  ContentContainer,
  CoverImageWrapper,
  Eyebrow,
  FormContainer,
  FormStickyWrapper,
  HeaderContainer,
  HeaderContentWrapper,
  HeaderTextWrapper,
  LiveIndicator,
  MainContentBackgroundContainer,
  MainContentWrapper,
  PageHeadline,
  WebinarSpeaker,
  WebinarSpeakerHeadline,
  WebinarSpeakerImage,
  WebinarSpeakerList,
  ResourceDownloadPageWrapper,
  VideoModal,
  VideoModalYouTubeVideo,
  RelatedContainer,
};

export default resourcePageComponents;
